import React, {useEffect, useState, useCallback} from 'react';
import { Switch, Route, useLocation } from 'react-router-dom'
import { IntlProvider } from 'react-intl'
import updateSecondLevelMenuActiveLink from 'updateSecondLevelMenuActiveLink'
import Homepage from "@Pages/Homepage";
import NotFound from "@Pages/NotFound";
import Branches from "@Pages/Branches";
import CityHub from "@Pages/CityHub";
import Overview from "@Pages/Overview";
import { Navigation, initEventsRel } from '@rel/experience';

// We have to collect all routes outside the App function, because of the pageLoader.
// If we use the pageLoader inside a function, SSR wont load the components.
let routeComponents = [];
// Load the routes for the specific country.
const routesCore = require(`../config/core/client.routes.json`);
let routesOpCo = {};
try {
  routesOpCo = require(`../config/${process.env.REACT_APP_COUNTRY}/client.routes.json`);
}
catch (e) {}
const routes = {
  ...routesCore,
  ...routesOpCo
};

const isMyRandstadEnabled = process.env.REACT_APP_MYRANDSTAD_ENABLED === 'true';
const isSavedJobIconEnabled = process.env.REACT_APP_ENABLE_SAVED_JOB_ICON === 'true';

// Iterate through all supported languages for the country.
for (let lang in routes) {
  // Iterate through all supported routes and components for the country.
  for (let routeName in routes[lang]) {
    const route = routes[lang][routeName];
    let Component = null;
    switch (route.component) {
      case 'homepage':
        Component = Homepage;
        break;
      case 'Branches':
        Component = Branches;
        break;
      case 'CityHub':
        Component = CityHub;
        break;
      case 'Overview':
        Component = Overview;
        break
      default:
        break;
    }

    // Add the route in the renderable array.
    routeComponents.push(<Route exact key={routeName} path={`${route.url}`} component={Component}/>)
  }
}
// Everything that is not defined as route will lead to NotFound page.
routeComponents.push(<Route key='not-found' component={NotFound}/>);
function App({currentLanguage, links, isEnableRelNavigation}) {
  // Get the language.
  const lang = currentLanguage ? currentLanguage : process.env.REACT_APP_DEFAULT_LANGUAGE;

  // Get the translations based on the language.
  let messages;
  try {
    messages = require(`../i18n/${process.env.REACT_APP_COUNTRY}/${lang}.json`)
  }
  catch (e) {
    // Use the translations from the default language if the file is missing.
    console.error(`Missing translation for: ${lang}`)
  }

  const logoUrl = currentLanguage === process.env.REACT_APP_DEFAULT_LANGUAGE ? '' : currentLanguage;

  const navigationProps = {
    ...isMyRandstadEnabled && {
      myRandstad: {
        text: 'my randstad',
        href: '',
        user: null,
      }
    },
    ...isSavedJobIconEnabled && {
      savedJobs: {
        text: '0',
        href: '/',
      },
    },
    logo: {
      title: messages['MegaMenu.Logo.Title'],
      href: `/${logoUrl}`,
    },
    aria: {
      navigation: {
        menu: messages['MegaMenu.Aria.Navigation.Menu'],
        navigation: messages['MegaMenu.Aria.Navigation.Navigation'],
        close: messages['MegaMenu.Aria.Navigation.Close'],
      },
      megaMenu: {
        main: messages['MegaMenu.Aria.MegaMenu.Main'],
        close: messages['MegaMenu.Aria.MegaMenu.Close'],
      },
    }
  };

  const [myRandstadProps, setMyRandstadProps] = useState(navigationProps.myRandstad);
  const [savedJobsProps, setSavedJobsProps] = useState(navigationProps.savedJobs);
  const [itemProps, setItemProps] = useState(links?.items || []);

  useEffect(()=> {
    document.querySelector('html').classList.add("js")
  },[])

  const updateNavigationProps = useCallback((newProps) => {
    if (isMyRandstadEnabled && newProps.myRandstad) {
      setMyRandstadProps(newProps.myRandstad);
    }

    if (newProps.myRandstadMenuItems) {
      setItemProps([...itemProps, (newProps.myRandstadMenuItems || {})]);
    }
  }, []);

  useEffect(() => {
    if (isMyRandstadEnabled) {
      setMyRandstadProps({
        text: window.myRandstadText,
        href: window.myRandstadUrl,
        user: window.myRandstadUsername ? {
          initials: window.myRandstadUsername?.charAt(0)?.toUpperCase(),
          names: window.myRandstadUsername,
        } : null,
      });
    }

    const updateSavedJobsCounter = () => setSavedJobsProps({
      text: window.relNavFavoritesCounter,
      href: window.relNavFavoritesUrl,
    });

    if (isSavedJobIconEnabled) {
      updateSavedJobsCounter();
      document.addEventListener('favoritesCounterUpdated', updateSavedJobsCounter);
    }

    // This event only dispatch in auth-widget of OPCOs it
    document.addEventListener('updateNavigationProps', (event) => {
      updateNavigationProps(event.detail);
    });

    return () => {
      document.removeEventListener('updateNavigationProps', (event) => {
        updateNavigationProps(event.details);
      });

      if (isSavedJobIconEnabled) {
        document.removeEventListener('favoritesCounterUpdated', updateSavedJobsCounter);
      }
    };
  }, []);

  /**
   * Implement datalayer events from REL for the Mega menu.
   */
  useEffect(() => {
    if (isEnableRelNavigation) {
      initEventsRel();
    }
  }, []);

  useEffect(() => {
    updateSecondLevelMenuActiveLink();
  }, [useLocation()]);

  return(
    <IntlProvider defaultLocale={process.env.REACT_APP_DEFAULT_LANGUAGE} locale={lang} messages={messages}>
      {isEnableRelNavigation && (
        <Navigation
          {...navigationProps}
          myRandstad={myRandstadProps}
          savedJobs={savedJobsProps}
          {...links}
          items={itemProps}
        />
      )}
      <Switch>
        {routeComponents}
      </Switch>
    </IntlProvider>
  )
}

export default App
